// Breakable glass on the client. The server's breaks (`GlassBroken`, `Welcome.brokenGlass`) go into the
// collision world the predictor shares, each from its break tick on, followed by a replay of the ticks already
// predicted (gr_core::predict). On screen the pane disappears in a burst of shards with a crash. The local
// player's own shots shatter a pane at once, picture only: collision waits for the server's break, and a pane
// the server never breaks comes back.

import type { AudioEngine } from "../audio";
import type { GlassBroken } from "../proto/gunrun/v1/game_pb";
import type { Renderer } from "../render";
import type { Vec3Like, WorldHandle } from "../wasm";

/** A pane shattered ahead of the server comes back after this long without the server's break (ms). */
const PREDICTED_BREAK_TIMEOUT_MS = 1500;
/** Shard speed along the local player's bullet, m/s (the server's `BULLET_PUSH`). */
const BULLET_PUSH = 3.5;
/** How far below a pane shards may fall when nothing is under it, m. */
const MAX_FALL = 12;

export interface GlassDeps {
  renderer: Renderer;
  audio: AudioEngine;
  /** The collision world changed under ticks the local player already predicted. */
  onWorldChanged(): void;
}

export class GlassController {
  private world: WorldHandle | null = null;
  /** Every pane the server broke on this map, with its break tick. */
  private readonly broken = new Map<number, number>();
  /** Panes shattered on screen ahead of the server, with the time (ms). */
  private readonly predicted = new Map<number, number>();

  constructor(private readonly d: GlassDeps) {}

  /** A new map (a `Welcome`): the panes broken before we joined. Applied once the map is loaded. */
  reset(brokenGlass: readonly number[]): void {
    this.world = null;
    this.broken.clear();
    this.predicted.clear();
    // Broken before we joined: gone for every tick we will simulate.
    for (const brush of brokenGlass) this.broken.set(brush, 0);
  }

  /** The map is loaded (world and pane meshes): every break known so far applies, without effects. */
  attach(world: WorldHandle): void {
    this.world = world;
    for (const [brush, tick] of this.broken) {
      world.breakBrush(brush, tick);
      this.d.renderer.glass?.hide(brush);
    }
  }

  onBroken(msg: GlassBroken): void {
    let changed = false;
    for (const pane of msg.panes) {
      if (this.broken.has(pane.brush)) continue;
      this.broken.set(pane.brush, msg.tick);
      if (this.world?.breakBrush(pane.brush, msg.tick)) changed = true;
      if (this.predicted.delete(pane.brush)) continue;
      if (this.world !== null) this.shatter(pane.brush, pane.at, pane.push);
    }
    if (changed) this.d.onWorldChanged();
  }

  onRestored(): void {
    this.broken.clear();
    this.predicted.clear();
    this.world?.restoreBreakables();
    this.d.renderer.glass?.restoreAll();
  }

  /** The local player's bullet passed pane `brush` at `at` along the unit `dir`: shatter it now. */
  predictBreak(brush: number, at: Vec3Like, dir: Vec3Like, nowMs: number): void {
    if (this.broken.has(brush) || this.predicted.has(brush)) return;
    if (this.shatter(brush, at, { x: dir.x * BULLET_PUSH, y: dir.y * BULLET_PUSH, z: dir.z * BULLET_PUSH })) {
      this.predicted.set(brush, nowMs);
    }
  }

  /** Per frame: panes shattered ahead of a server that never broke them come back. */
  frame(nowMs: number): void {
    if (this.predicted.size === 0) return;
    for (const [brush, at] of this.predicted) {
      if (nowMs - at < PREDICTED_BREAK_TIMEOUT_MS) continue;
      this.predicted.delete(brush);
      this.d.renderer.glass?.show(brush);
    }
  }

  clear(): void {
    this.reset([]);
  }

  /** Hides pane `brush` and bursts it; false when the map has no such pane or it is hidden already. */
  private shatter(brush: number, at: Vec3Like | undefined, push: Vec3Like | undefined): boolean {
    const panes = this.d.renderer.glass;
    const box = panes?.box(brush);
    if (!panes || !box || !panes.hide(brush)) return false;
    const center = { x: (box.min.x + box.max.x) / 2, y: (box.min.y + box.max.y) / 2, z: (box.min.z + box.max.z) / 2 };
    const hit = at ?? center;
    // Shards fall to the first surface under the pane (the pane itself no longer collides).
    const below = this.world?.raycast(center.x, box.min.y - 0.02, center.z, 0, -1, 0, MAX_FALL);
    const floorY = below ? below.pos.y : box.min.y - MAX_FALL;
    this.d.renderer.effects.shatter(box.min, box.max, hit, push ?? { x: 0, y: 0, z: 0 }, floorY);
    this.d.audio.playGlassBreak(hit);
    return true;
  }
}
